// Vedic Math Interactive Tutorials - Part 2
// Square Roots, Cube Roots, Linear Equations

import { TechniqueBreakdown } from "../techniqueBreakdowns";

export const vedicMathTutorials: Record<string, TechniqueBreakdown> = {
  "c9d0e1f2-a3b4-5c6d-7e8f-90a1b2c3d4e5": {
    title: "Finding Square Roots Using Vedic Math",
    description:
      "Learn to find square roots mentally using digit grouping and estimation!",
    steps: [
      {
        stepNumber: 1,
        title: "Identify Last Digit Pattern",
        explanation:
          "The last digit of a perfect square tells you the last digit of its root: 1→1/9, 4→2/8, 9→3/7, 6→4/6, 5→5, 0→0.",
        practice: {
          problem: "√1296",
          question: "Last digit is 6. What are possible last digits of √1296?",
          answer: "4 or 6",
          hint: "Digit 6 comes from 4² or 6²",
        },
      },
      {
        stepNumber: 2,
        title: "Group Digits and Estimate",
        explanation:
          "Group digits in pairs from right. Find perfect squares near the left group to estimate.",
        practice: {
          problem: "√1296",
          question: "Group: 12|96. Which perfect square is close to 12?",
          answer: "9",
          hint: "3²=9, 4²=16. So 12 is between 3² and 4²",
          intermediateSteps: [
            { label: "Left group", answer: "12" },
            { label: "Perfect squares near 12", answer: "9 and 16" },
            {
              label: "Tens digit",
              answer: "3",
              hint: "Between 3 and 4, choose lower: 3",
            },
          ],
        },
      },
      {
        stepNumber: 3,
        title: "Combine and Verify",
        explanation:
          "Combine tens digit estimate with last digit options. Test which works.",
        practice: {
          problem: "√1296",
          question: "Options: 34 or 36. Which one when squared gives 1296?",
          answer: "36",
          hint: "36² = 1296 ✓",
          intermediateSteps: [
            { label: "Test 34", answer: "1156", hint: "34² = 1156 ✗" },
            { label: "Test 36", answer: "1296", hint: "36² = 1296 ✓" },
          ],
        },
      },
    ],
    finalExample: {
      problem: "√1296",
      fullSolution:
        "36 (Last digit 6→4/6, Group 12|96→tens=3, Test: 36²=1296 ✓)",
    },
  },

  "d0e1f2a3-b4c5-6d7e-8f90-a1b2c3d4e5f6": {
    title: "Finding Cube Roots Using Vedic Math",
    description: "Master cube roots with last digit patterns and grouping!",
    steps: [
      {
        stepNumber: 1,
        title: "Last Digit Cube Pattern",
        explanation:
          "Each digit has unique cube ending: 1→1, 2→8, 3→7, 4→4, 5→5, 6→6, 7→3, 8→2, 9→9, 0→0.",
        practice: {
          problem: "∛13824",
          question: "Last digit is 4. What is last digit of ∛13824?",
          answer: "4",
          hint: "Only 4³ ends in 4",
        },
      },
      {
        stepNumber: 2,
        title: "Group in Threes",
        explanation:
          "Group digits in sets of 3 from right. Left group tells you the tens digit range.",
        practice: {
          problem: "∛13824",
          question: "Group: 13|824. Which cube is near 13?",
          answer: "8",
          hint: "2³=8, 3³=27. So 13 is between them",
          intermediateSteps: [
            { label: "Left group", answer: "13" },
            { label: "Nearby cubes", answer: "8 (2³) and 27 (3³)" },
            { label: "Tens digit", answer: "2", hint: "Choose lower: 2" },
          ],
        },
      },
      {
        stepNumber: 3,
        title: "Combine Digits",
        explanation:
          "Put together: tens digit from grouping + ones digit from pattern.",
        practice: {
          problem: "∛13824",
          question: "Combine tens=2 and ones=4. What is ∛13824?",
          answer: "24",
          hint: "Verify: 24³ = 13824 ✓",
        },
      },
    ],
    finalExample: {
      problem: "∛13824",
      fullSolution: "24 (Last digit 4→4, Group 13|824→tens=2, Answer: 24)",
    },
  },

  "e1f2a3b4-c5d6-7e8f-90a1-b2c3d4e5f6a7": {
    title: "Linear Equations - Transpose and Apply",
    description:
      'Paravartya Yojayet means "transpose and apply". Solve equations by reversing operations!',
    steps: [
      {
        stepNumber: 1,
        title: "Identify the Pattern",
        explanation:
          "For ax + b = c, recognize coefficient (a), constant (b), and result (c).",
        practice: {
          problem: "3x + 5 = 17",
          question: "What are a, b, and c?",
          answer: "a=3, b=5, c=17",
          hint: "Pattern: ax + b = c",
        },
      },
      {
        stepNumber: 2,
        title: "Transpose (Reverse Operations)",
        explanation:
          "Move b to other side by reversing: + becomes -, - becomes +.",
        practice: {
          problem: "3x + 5 = 17",
          question: "Transpose +5. What equation do you get?",
          answer: "3x = 12",
          hint: "17 - 5 = 12",
          intermediateSteps: [
            { label: "Original", answer: "3x + 5 = 17" },
            { label: "Transpose +5", answer: "3x = 17 - 5" },
            { label: "Simplify", answer: "3x = 12" },
          ],
        },
      },
      {
        stepNumber: 3,
        title: "Apply (Solve for x)",
        explanation: "Divide both sides by coefficient a to isolate x.",
        practice: {
          problem: "3x = 12",
          question: "Divide by 3. What is x?",
          answer: "4",
          hint: "12 ÷ 3 = 4",
          intermediateSteps: [
            { label: "Equation", answer: "3x = 12" },
            { label: "Divide by 3", answer: "x = 12/3" },
            { label: "Solution", answer: "x = 4" },
          ],
        },
      },
    ],
    finalExample: {
      problem: "3x + 5 = 17",
      fullSolution: "x = 4 (Transpose: 3x=12, Apply: x=12÷3=4)",
    },
  },

  "2436fc53-b862-4c81-b130-69b116b6333a": {
    title: "Addition Dot Method",
    description:
      "Column addition that avoids mentally carrying large numbers by using dots to track carries!",
    steps: [
      {
        stepNumber: 1,
        title: "Add Normally Until Sum > 9",
        explanation:
          "Add numbers in a column from top to bottom. When your running total exceeds 9, place a dot next to the number you just added.",
        practice: {
          problem: "Add column: 8, 4, 5",
          question:
            "Start with 8, add 4. Total is 12 (>9). Place a dot. What digit do you write down?",
          answer: "2",
          hint: "8+4=12. Write 2, put a dot next to the 4. The dot reminds you that you carried 1 ten.",
          intermediateSteps: [
            { label: "8 + 4", answer: "12" },
            { label: "Write down", answer: "2 (ones digit)" },
            { label: "Place dot", answer: "• next to 4" },
          ],
        },
      },
      {
        stepNumber: 2,
        title: "Continue Adding from Written Digit",
        explanation:
          "Continue from the digit you wrote (not the full sum). Add the next number.",
        practice: {
          problem: "You have 2 (from 8+4=12•), now add 5",
          question: "Add 5 to 2. What is your new total?",
          answer: "7",
          hint: "2 + 5 = 7",
          intermediateSteps: [
            { label: "Current digit", answer: "2" },
            { label: "Add next number", answer: "2 + 5" },
            { label: "New total", answer: "7" },
          ],
        },
      },
      {
        stepNumber: 3,
        title: "Count Dots for Carry",
        explanation:
          "At the end, count all dots in the column. This is your carry to the next column.",
        practice: {
          problem: "Column sum: 7, Dots: 1",
          question: "How many do you carry to the next column?",
          answer: "1",
          hint: "Each dot = 1 ten to carry",
          intermediateSteps: [
            { label: "Column result", answer: "7" },
            { label: "Dots counted", answer: "1 dot" },
            { label: "Carry over", answer: "1" },
          ],
        },
      },
      {
        stepNumber: 4,
        title: "Try a Full Example",
        explanation:
          "Practice with multiple numbers where sums exceed 9 multiple times.",
        practice: {
          problem: "Add: 7 + 6 + 8 + 5",
          question:
            "Using dot method, what is the final digit and how many dots?",
          answer: "6 and 2 dots",
          hint: "7+6=13•→3, 3+8=11•→1, 1+5=6. Total: 6 with 2 dots = carry 2",
          intermediateSteps: [
            { label: "7 + 6 = 13", answer: "Write 3, dot •" },
            { label: "3 + 8 = 11", answer: "Write 1, dot •" },
            { label: "1 + 5 = 6", answer: "Write 6" },
            { label: "Final", answer: "6 (digit) + 2 (dots) = 26" },
          ],
        },
      },
    ],
    finalExample: {
      problem: "8 + 4 + 5 = ?",
      fullSolution: "17 (8+4=12•→2, 2+5=7, count dots: 1, carry 1: answer 17)",
    },
  },

  "cd636170-b5ae-4a43-81d4-10b2c07ad01c": {
    title: "Left to Right Addition",
    description:
      "Add from left to right, starting with the largest place values - just like reading!",
    steps: [
      {
        stepNumber: 1,
        title: "Identify Place Values",
        explanation:
          "Look at both numbers and identify the place values: hundreds, tens, ones. For Problem: 568 + 394",
        practice: {
          problem: "568 + 394",
          question: "What are the hundreds digits?",
          answer: "5 and 3",
          hint: "568 has 5 hundreds, 394 has 3 hundreds",
          intermediateSteps: [
            { label: "First number", answer: "5" },
            { label: "Second number", answer: "3" },
          ],
        },
      },
      {
        stepNumber: 2,
        title: "Add Hundreds First",
        explanation:
          "Start with the largest place value (hundreds). Add them together.For Problem: 568 + 394",
        practice: {
          problem: "500 + 300",
          question: "What is 500 + 300?",
          answer: "800",
          hint: "5 hundreds + 3 hundreds = 8 hundreds = 800",
        },
      },
      {
        stepNumber: 3,
        title: "Add Tens Next",
        explanation:
          "Move to the tens place and add those values.For Problem: 568 + 394",
        practice: {
          problem: "60 + 90",
          question: "What is 60 + 90?",
          answer: "150",
          hint: "6 tens + 9 tens = 15 tens = 150",
          intermediateSteps: [
            { label: "Hundreds total", answer: "800" },
            { label: "Tens", answer: "150" },
            { label: "Running total", answer: "950" },
          ],
        },
      },
      {
        stepNumber: 4,
        title: "Add Ones and Combine",
        explanation:
          "Finally add the ones place, then combine all your totals.For Problem: 568 + 394",
        practice: {
          problem: "8 + 4",
          question:
            "Add the ones (8+4), then add to 950. What is the final answer?",
          answer: "962",
          hint: "8 + 4 = 12, then 950 + 12 = 962",
          intermediateSteps: [
            { label: "Hundreds", answer: "800" },
            { label: "Tens", answer: "150" },
            { label: "Ones", answer: "12" },
            { label: "Total", answer: "962" },
          ],
        },
      },
    ],
    finalExample: {
      problem: "568 + 394",
      fullSolution:
        "962 (500+300=800, 60+90=150, 8+4=12, Total: 800+150+12=962)",
    },
  },

  "b0c1d2e3-f4a5-6a7b-8c9d-aefbdcedf012": {
    title: "Generating Pythagorean Triples",
    description:
      "Create right-angle triangle sides instantly using a simple formula!",
    steps: [
      {
        stepNumber: 1,
        title: "Understanding Pythagorean Triples",
        explanation:
          "A Pythagorean triple is three numbers (a, b, c) where a² + b² = c². Famous example: 3, 4, 5 because 3² + 4² = 9 + 16 = 25 = 5².",
        practice: {
          problem: "Verify: 5, 12, 13",
          question: "Calculate: 5² + 12² = ?",
          answer: "169",
          hint: "5² = 25, 12² = 144, so 25 + 144 = 169",
          intermediateSteps: [
            { label: "5²", answer: "25" },
            { label: "12²", answer: "144" },
            { label: "Sum", answer: "169" },
            { label: "13²", answer: "169 ✓ Match!" },
          ],
        },
      },
      {
        stepNumber: 2,
        title: "Pick Two Numbers (a and b)",
        explanation:
          "Start by picking two numbers where a > b. Let's use a=3 and b=2.",
        practice: {
          problem: "Generate triple with a=3, b=2",
          question: "What is a² = 3²?",
          answer: "9",
          hint: "3 × 3 = 9",
        },
      },
      {
        stepNumber: 3,
        title: "Calculate First Side: a² - b²",
        explanation: "The first side of the triangle is a² - b².",
        practice: {
          problem: "a=3, b=2",
          question: "What is a² - b² = 9 - 4?",
          answer: "5",
          hint: "3² - 2² = 9 - 4 = 5",
          intermediateSteps: [
            { label: "a² = 3²", answer: "9" },
            { label: "b² = 2²", answer: "4" },
            { label: "First side", answer: "9 - 4 = 5" },
          ],
        },
      },
      {
        stepNumber: 4,
        title: "Calculate Second Side: 2ab",
        explanation: "The second side is 2 × a × b.",
        practice: {
          problem: "a=3, b=2",
          question: "What is 2 × 3 × 2?",
          answer: "12",
          hint: "2 × 3 × 2 = 12",
          intermediateSteps: [
            { label: "2 × a", answer: "2 × 3 = 6" },
            { label: "6 × b", answer: "6 × 2 = 12" },
            { label: "Second side", answer: "12" },
          ],
        },
      },
      {
        stepNumber: 5,
        title: "Calculate Hypotenuse: a² + b²",
        explanation: "The hypotenuse (longest side) is a² + b².",
        practice: {
          problem: "a=3, b=2",
          question: "What is a² + b² = 9 + 4?",
          answer: "13",
          hint: "9 + 4 = 13",
          intermediateSteps: [
            { label: "Side 1 (a²-b²)", answer: "5" },
            { label: "Side 2 (2ab)", answer: "12" },
            { label: "Hypotenuse (a²+b²)", answer: "13" },
            { label: "Pythagorean triple", answer: "(5, 12, 13) ✓" },
          ],
        },
      },
    ],
    finalExample: {
      problem: "Generate triple with a=4, b=1",
      fullSolution: "(15, 8, 17): 4²-1²=15, 2×4×1=8, 4²+1²=17",
    },
  },

  "d2e3f4a5-b6c7-8c9d-0e1f-bdcedf012345": {
    title: "Volume by Decomposition",
    description:
      "Break complex 3D shapes into simpler parts to calculate volume easily!",
    steps: [
      {
        stepNumber: 1,
        title: "Understanding Decomposition",
        explanation:
          "Complex shapes can be broken into simpler pieces like cubes, rectangular prisms, or cylinders. Calculate each part, then add or subtract.",
        practice: {
          problem: "L-shape made of two boxes",
          question: "How many simple shapes do we have?",
          answer: "2",
          hint: "Count each distinct rectangular prism",
        },
      },
      {
        stepNumber: 2,
        title: "Volume of Rectangular Prism",
        explanation:
          "For a box: V = length × width × height. Always multiply all three dimensions.",
        practice: {
          problem: "Box: 5cm × 3cm × 2cm",
          question: "What is the volume?",
          answer: "30",
          hint: "5 × 3 × 2 = 30 cm³",
          intermediateSteps: [
            { label: "5 × 3", answer: "15" },
            { label: "15 × 2", answer: "30 cm³" },
          ],
        },
      },
      {
        stepNumber: 3,
        title: "Adding Volumes Together",
        explanation: "When shapes are combined, add their volumes.",
        practice: {
          problem: "L-shape: vertical part 4×2×2cm, horizontal part 6×2×2cm",
          question: "What is volume of vertical part?",
          answer: "16",
          hint: "4 × 2 × 2 = 16 cm³",
          intermediateSteps: [
            { label: "Vertical", answer: "4×2×2 = 16 cm³" },
            { label: "Horizontal", answer: "6×2×2 = 24 cm³" },
            { label: "Next step", answer: "Add them together" },
          ],
        },
      },
      {
        stepNumber: 4,
        title: "Complete the Calculation",
        explanation: "Add all the volumes together to get the total.",
        practice: {
          problem: "Vertical: 16 cm³, Horizontal: 24 cm³",
          question: "What is the total volume?",
          answer: "40",
          hint: "16 + 24 = 40 cm³",
          intermediateSteps: [
            { label: "Vertical part", answer: "16 cm³" },
            { label: "Horizontal part", answer: "24 cm³" },
            { label: "Total volume", answer: "40 cm³" },
          ],
        },
      },
      {
        stepNumber: 5,
        title: "Subtracting for Hollow Parts",
        explanation:
          "When there's a hole, subtract that volume. Example: solid cube with a hole cut out.",
        practice: {
          problem: "Cube 5×5×5 with hole 2×2×5 removed",
          question: "What is the remaining volume?",
          answer: "105",
          hint: "Cube: 125 cm³, Hole: 20 cm³, Remaining: 125-20=105",
          intermediateSteps: [
            { label: "Solid cube", answer: "5×5×5 = 125 cm³" },
            { label: "Hole removed", answer: "2×2×5 = 20 cm³" },
            { label: "Subtract", answer: "125 - 20 = 105 cm³" },
          ],
        },
      },
    ],
    finalExample: {
      problem: "T-shape: top bar 10×2×2cm, vertical 6×2×2cm",
      fullSolution: "64 cm³ (Top: 40, Vertical: 24, Total: 64)",
    },
  },

  "a1b2c3d4-e5f6-4a5b-8c9d-1e2f3a4b5c6d": {
    title: "All from 9, Last from 10",
    description:
      "Nikhilam Navatashcaramam Dashatah - Find ten's complement instantly by subtracting digits from 9 and last from 10!",
    steps: [
      {
        stepNumber: 1,
        title: "Understanding Ten's Complement",
        explanation:
          "The ten's complement tells us how far a number is from the next power of 10 (10, 100, 1000, etc.). It's what we add to reach that power of 10.",
        practice: {
          problem: "456 + ? = 1,000",
          question: "What number added to 456 gives 1,000?",
          answer: "544",
          hint: "1,000 - 456 = 544. This is the ten's complement of 456.",
          intermediateSteps: [
            { label: "Target", answer: "1,000 (next power of 10)" },
            { label: "Number", answer: "456" },
            { label: "Complement", answer: "544" },
          ],
        },
      },
      {
        stepNumber: 2,
        title: "All from 9 (Except Last)",
        explanation:
          'For every digit EXCEPT the last one, subtract from 9. This is the "all from 9" part of Nikhilam.',
        practice: {
          problem: "1,000 - 456",
          question: "For 456, what is 4 from 9? And 5 from 9?",
          answer: "5 and 4",
          hint: "9 - 4 = 5, and 9 - 5 = 4. We do this for all digits except the last.",
          intermediateSteps: [
            { label: "First digit: 4", answer: "9 - 4 = 5" },
            { label: "Second digit: 5", answer: "9 - 5 = 4" },
            { label: "Last digit: 6", answer: "Handle separately" },
          ],
        },
      },
      {
        stepNumber: 3,
        title: "Last from 10",
        explanation:
          "For the LAST digit only, subtract from 10 instead of 9. This completes the complement.",
        practice: {
          problem: "1,000 - 456 (last digit)",
          question: "What is 6 from 10?",
          answer: "4",
          hint: "10 - 6 = 4. This is the last digit of our answer.",
          intermediateSteps: [
            { label: "Last digit", answer: "6" },
            { label: "Subtract from", answer: "10 (not 9!)" },
            { label: "Result", answer: "10 - 6 = 4" },
          ],
        },
      },
      {
        stepNumber: 4,
        title: "Combine All Parts",
        explanation:
          'Put together all the results: digits from "all from 9" followed by the "last from 10" digit.',
        practice: {
          problem: "1,000 - 456",
          question: "Combine: (4→5), (5→4), (6→4). What is the answer?",
          answer: "544",
          hint: "Read left to right: 5, 4, 4 = 544",
          intermediateSteps: [
            { label: "4 from 9", answer: "5" },
            { label: "5 from 9", answer: "4" },
            { label: "6 from 10", answer: "4" },
            { label: "Final answer", answer: "544" },
          ],
        },
      },
      {
        stepNumber: 5,
        title: "Practice with Larger Numbers",
        explanation:
          "The method works the same for any size number. Just remember: all from 9, last from 10!",
        practice: {
          problem: "10,000 - 3,859",
          question: "Apply Nikhilam: What is the complement?",
          answer: "6141",
          hint: "3→6, 8→1, 5→4, 9→1 = 6,141",
          intermediateSteps: [
            { label: "3 from 9", answer: "6" },
            { label: "8 from 9", answer: "1" },
            { label: "5 from 9", answer: "4" },
            { label: "9 from 10", answer: "1" },
            { label: "Answer", answer: "6,141" },
          ],
        },
      },
    ],
    finalExample: {
      problem: "1,000 - 456",
      fullSolution: "544 (4→5, 5→4, 6→4: All from 9, last from 10)",
    },
  },

  "d0c74bae-8e16-4693-9b77-14cbbfba0e30": {
    title: "Subtraction by Complements",
    description:
      "Transform difficult subtraction into easy addition using ten's complement! No more borrowing.",
    steps: [
      {
        stepNumber: 1,
        title: "The Big Idea",
        explanation:
          "Instead of subtracting directly, we can add the complement and remove the excess. This avoids all borrowing!",
        practice: {
          problem: "82 - 47",
          question: "What is the complement of 47 from 100?",
          answer: "53",
          hint: "Use Nikhilam: 4→5, 7→3 = 53",
          intermediateSteps: [
            { label: "Subtrahend", answer: "47" },
            { label: "4 from 9", answer: "5" },
            { label: "7 from 10", answer: "3" },
            { label: "Complement", answer: "53" },
          ],
        },
      },
      {
        stepNumber: 2,
        title: "Add the Complement",
        explanation:
          "Add the complement to the minuend. This is much easier than subtracting!",
        practice: {
          problem: "82 - 47 (using complement)",
          question: "Add: 82 + 53 = ?",
          answer: "135",
          hint: "Simple addition: 82 + 53 = 135",
          intermediateSteps: [
            { label: "Original", answer: "82 - 47" },
            { label: "Complement of 47", answer: "53" },
            { label: "Add instead", answer: "82 + 53 = 135" },
          ],
        },
      },
      {
        stepNumber: 3,
        title: "Drop the Excess",
        explanation:
          "The result will be 100 (or 1000, 10000, etc.) more than the answer. Drop that excess by removing the leading 1.",
        practice: {
          problem: "82 - 47 = ?",
          question: "You got 135. Drop the 100. What remains?",
          answer: "35",
          hint: "135 - 100 = 35. Just drop the leading 1!",
          intermediateSteps: [
            { label: "Sum with complement", answer: "135" },
            { label: "Excess (power of 10)", answer: "100" },
            { label: "Drop the 1", answer: "35" },
            { label: "Verify", answer: "82 - 47 = 35 ✓" },
          ],
        },
      },
      {
        stepNumber: 4,
        title: "Practice with 3-Digit Numbers",
        explanation:
          "Same method works for larger numbers. Find complement from 1,000, add, then drop 1,000.",
        practice: {
          problem: "453 - 178",
          question: "Complement of 178 from 1,000?",
          answer: "822",
          hint: "1→8, 7→2, 8→2 = 822",
          intermediateSteps: [
            { label: "Subtrahend", answer: "178" },
            { label: "1 from 9", answer: "8" },
            { label: "7 from 9", answer: "2" },
            { label: "8 from 10", answer: "2" },
            { label: "Complement", answer: "822" },
          ],
        },
      },
      {
        stepNumber: 5,
        title: "Complete the Subtraction",
        explanation:
          "Add the complement, then drop the excess. Much easier than traditional borrowing!",
        practice: {
          problem: "453 - 178",
          question: "453 + 822 = 1,275. Drop 1,000. Answer?",
          answer: "275",
          hint: "1,275 - 1,000 = 275",
          intermediateSteps: [
            { label: "Original problem", answer: "453 - 178" },
            { label: "Add complement", answer: "453 + 822 = 1,275" },
            { label: "Drop 1,000", answer: "275" },
            { label: "Verify", answer: "453 - 178 = 275 ✓" },
          ],
        },
      },
    ],
    finalExample: {
      problem: "453 - 178",
      fullSolution: "275 (Complement: 822, Add: 1,275, Drop 1,000)",
    },
  },

  // Paravartya Division
  "e6a7b8c9-d0e1-f2a3-b4c5-d6e7f8a9b0c1": {
    title: "Paravartya Division - Transpose and Apply",
    description:
      'Paravartya Yojayet means "transpose and apply". Divide by multi-digit numbers systematically without guessing quotient digits!',
    steps: [
      {
        stepNumber: 1,
        title: "Setup: Create the Transpose",
        explanation:
          'Write the divisor, keeping the first digit. Write remaining digits below with NEGATIVE (-) signs. This "transpose" is your working tool.',
        tips: [
          "The transpose handles all the subtraction automatically",
          "Negative signs are crucial - they create the feedback mechanism",
        ],
        practice: {
          problem: "Divisor: 12",
          question: "What is the transpose?",
          answer: "-2",
          hint: "Keep first digit (1), write second digit with negative sign: -2",
        },
      },
      {
        stepNumber: 2,
        title: "Setup: Split the Dividend",
        explanation:
          "Divide your dividend into Working part and Remainder part. Set aside (n-1) digits for remainder, where n = number of digits in divisor.",
        tips: [
          "Divisor has 2 digits? Set aside 1 digit",
          "Divisor has 3 digits? Set aside 2 digits",
          "Divisor has 5 digits? Set aside 4 digits",
        ],
        practice: {
          problem: "1225 ÷ 12 (divisor has 2 digits)",
          question: "How many digits to set aside for remainder?",
          answer: "1",
          hint: "Divisor has 2 digits, so set aside 2-1 = 1 digit. Working: 122 | Remainder: 5",
          intermediateSteps: [
            { label: "Divisor digits", answer: "2", hint: "12 has 2 digits" },
            { label: "Set aside", answer: "1", hint: "n-1 = 2-1 = 1" },
            {
              label: "Split",
              answer: "122|5",
              hint: "Working part: 122, Remainder part: 5",
            },
          ],
        },
      },
      {
        stepNumber: 3,
        title: "Process: Bring Down First Digit",
        explanation:
          "The first working digit becomes your first quotient digit. Simply bring it down - no calculation needed!",
        practice: {
          problem: "1225 ÷ 12, Working part: 122",
          question: "What is the first quotient digit?",
          answer: "1",
          hint: "Just bring down the first working digit: 1",
        },
      },
      {
        stepNumber: 4,
        title: "Process: Multiply by Transpose",
        explanation:
          "Multiply each quotient digit by the transpose value(s). Add these products to the digits on the right.",
        tips: [
          "Work left to right through the working digits",
          "Each sum becomes the next quotient digit",
          "Carries happen automatically!",
        ],
        practice: {
          problem: "1225 ÷ 12, First quotient digit: 1, Transpose: -2",
          question: "Calculate 1 × (-2), then add to next digit (2)",
          answer: "0",
          hint: "1 × (-2) = -2. Add to 2: 2 + (-2) = 0. This is your second quotient digit!",
          intermediateSteps: [
            {
              label: "1 × (-2)",
              answer: "-2",
              hint: "Multiply quotient digit by transpose",
            },
            {
              label: "2 + (-2)",
              answer: "0",
              hint: "Add product to next working digit",
            },
            {
              label: "Second Q digit",
              answer: "0",
              hint: "The sum becomes next quotient digit",
            },
          ],
        },
      },
      {
        stepNumber: 5,
        title: "Process: Continue Through Working Digits",
        explanation:
          "Repeat the multiply-and-add process for each working digit until you reach the remainder section.",
        practice: {
          problem: "1225 ÷ 12, Working: 122|5, Q so far: 10",
          question:
            "Multiply second quotient digit (0) by transpose (-2), add to third working digit (2)",
          answer: "2",
          hint: "0 × (-2) = 0. Add to 2: 2 + 0 = 2. Third quotient digit = 2",
          intermediateSteps: [
            {
              label: "0 × (-2)",
              answer: "0",
              hint: "Second Q digit × transpose",
            },
            { label: "2 + 0", answer: "2", hint: "Add to third working digit" },
            {
              label: "Third Q digit",
              answer: "2",
              hint: "Quotient so far: 102",
            },
          ],
        },
      },
      {
        stepNumber: 6,
        title: "Process: Calculate Remainder",
        explanation:
          "Apply the transpose to the remainder section. The final sum is your remainder.",
        practice: {
          problem: "1225 ÷ 12, Q: 102, Last Q digit: 2, Remainder section: 5",
          question: "Calculate 2 × (-2), add to 5",
          answer: "1",
          hint: "2 × (-2) = -4. Add to 5: 5 + (-4) = 1. This is your remainder!",
          intermediateSteps: [
            {
              label: "2 × (-2)",
              answer: "-4",
              hint: "Last quotient digit × transpose",
            },
            {
              label: "5 + (-4)",
              answer: "1",
              hint: "Add to remainder section",
            },
            { label: "Final R", answer: "1", hint: "Remainder = 1" },
          ],
        },
      },
      {
        stepNumber: 7,
        title: "Advanced: Three-Digit Divisors",
        explanation:
          "For divisors with 3+ digits, transpose has multiple values. Apply each one sequentially to digits on the right.",
        tips: [
          "Divisor 123 → transpose: -2, -3",
          "Each quotient digit gets multiplied by ALL transpose values",
          "Set aside (n-1) digits: 3-digit divisor means set aside 2 digits",
        ],
        practice: {
          problem: "2598 ÷ 123, Transpose: -2, -3",
          question: "For first quotient digit 2, what products do you add?",
          answer: "2×(-2)=-4 to next digit, 2×(-3)=-6 to digit after",
          hint: "Multiply 2 by BOTH transpose values: 2×(-2)=-4 affects first digit right, 2×(-3)=-6 affects second digit right",
          intermediateSteps: [
            {
              label: "Setup",
              answer: "25|98",
              hint: "Working: 25, Remainder: 98 (set aside 2 digits)",
            },
            { label: "2 × (-2)", answer: "-4", hint: "Add -4 to 5" },
            {
              label: "5 + (-4)",
              answer: "1",
              hint: "Second quotient digit = 1",
            },
            {
              label: "2 × (-3)",
              answer: "-6",
              hint: "Add -6 to first remainder digit (9)",
            },
          ],
        },
      },
      {
        stepNumber: 8,
        title: "Special Case: Negative Remainders",
        explanation:
          "If remainder becomes negative, borrow 1 from the quotient and add the full divisor to the remainder.",
        tips: [
          "This happens occasionally - it's normal!",
          "Borrowing always converts negative to positive",
          "Final answer: (Q-1) with remainder (divisor + negative_remainder)",
        ],
        practice: {
          problem: "13456 ÷ 1123 gives Q=12, R=-20",
          question: "Fix the negative remainder",
          answer: "Q=11, R=1103",
          hint: "Borrow: 12-1=11. Add divisor: 1123+(-20)=1103. Final: Q=11, R=1103",
          intermediateSteps: [
            {
              label: "Initial result",
              answer: "Q=12, R=-20",
              hint: "Remainder is negative!",
            },
            {
              label: "Borrow from Q",
              answer: "12-1=11",
              hint: "Subtract 1 from quotient",
            },
            {
              label: "Add divisor to R",
              answer: "1123+(-20)=1103",
              hint: "Add full divisor to negative remainder",
            },
            {
              label: "Final answer",
              answer: "Q=11, R=1103",
              hint: "Positive remainder!",
            },
          ],
        },
      },
    ],
    finalExample: {
      problem: "1697 ÷ 14",
      fullSolution:
        "Divisor: 14, Transpose: -4 | Dividend: 169|7\nQ digits: 1 → 2 → 1 (apply transpose at each step)\nRemainder: 3\nAnswer: 121 R 3",
    },
  },
  "b0d20afe-ad25-48f4-97af-4af08b6e372f": {
    steps: [
      {
        title: "Step 1: Multiply the Right Digits",
        explanation:
          "First, multiply the rightmost (ones) digits of both numbers. This gives you the ones place of your answer. If the result is 10 or more, keep the ones digit and carry the tens digit to the next step.",
        practice: {
          problem: "23 × 14",
          question:
            "What do you get when you multiply the right digits (3 × 4)?",
          answer: "12",
          hint: "Multiply the last digit of each number: 3 × 4 = 12. Write down 2, carry 1.",
        },
      },
      {
        title: "Step 2: Criss-Cross Multiplication",
        explanation:
          "Now do the cross-multiplication: multiply the left digit of the first number by the right digit of the second, and the right digit of the first by the left digit of the second. Add these products together, plus any carry from Step 1.",
        practice: {
          problem: "23 × 14",
          question:
            "Calculate the criss-cross: (2×4) + (3×1) + carry from previous step",
          answer: "12",
          hint: "(2 × 4) + (3 × 1) + 1 (carry) = 8 + 3 + 1 = 12. Write down 2, carry 1.",
          intermediateSteps: [
            {
              label: "2 × 4",
              answer: "8",
              hint: "Left of first × Right of second",
            },
            {
              label: "3 × 1",
              answer: "3",
              hint: "Right of first × Left of second",
            },
            {
              label: "Sum + carry",
              answer: "12",
              hint: "8 + 3 + 1 (carry from step 1) = 12",
            },
          ],
        },
      },
      {
        title: "Step 3: Multiply the Left Digits",
        explanation:
          "Finally, multiply the leftmost (tens) digits together and add any carry from Step 2. This gives you the leftmost part of your answer.",
        practice: {
          problem: "23 × 14",
          question: "What is (2 × 1) + carry from previous step?",
          answer: "3",
          hint: "2 × 1 = 2, plus 1 (carry) = 3. This is the final part.",
          intermediateSteps: [
            { label: "2 × 1", answer: "2", hint: "Multiply the left digits" },
            {
              label: "Add carry",
              answer: "3",
              hint: "2 + 1 (carry from step 2) = 3",
            },
          ],
        },
      },
    ],
    finalExample: {
      problem: "23 × 14",
      fullSolution: "322 (Step 1: 2, Step 2: 2, Step 3: 3)",
    },
  },

  "9677e7bd-7656-42ab-90a6-f0757acc9bac": {
    steps: [
      {
        title: "Step 1: Find Distance from Base",
        explanation:
          "When multiplying numbers close to 100, find how far each number is from 100. If below 100, the distance is negative; if above 100, it's positive.",
        practice: {
          problem: "96 × 98",
          question: "How far is 96 from 100? How far is 98 from 100?",
          answer: "-4, -2",
          hint: "96 is 4 below 100 (-4), and 98 is 2 below 100 (-2)",
          intermediateSteps: [
            { label: "100 - 96", answer: "-4", hint: "96 is 4 below the base" },
            { label: "100 - 98", answer: "-2", hint: "98 is 2 below the base" },
          ],
        },
      },
      {
        title: "Step 2: Multiply the Distances",
        explanation:
          "Multiply the two distances together. This gives you the last 2 digits of your answer. If the result is a single digit, add a leading zero (like 08).",
        practice: {
          problem: "96 × 98",
          question: "What is (-4) × (-2)?",
          answer: "08",
          hint: "-4 × -2 = 8, written as 08 (two digits)",
        },
      },
      {
        title: "Step 3: Cross-Subtract",
        explanation:
          "Take either original number and subtract the OTHER number's distance from base. You can use 96 - (-2) = 96 + 2, or 98 - (-4) = 98 + 4. Both give the same answer!",
        practice: {
          problem: "96 × 98",
          question: "What is 96 - (-2)? (Or equivalently, 98 - (-4))",
          answer: "94",
          hint: "96 - (-2) = 96 + 2 = 98. Or 98 - (-4) = 98 + 4 = 102. Wait, that's wrong! Let me fix: 96 - 2 = 94 or 98 - 4 = 94",
        },
      },
    ],
    finalExample: {
      problem: "96 × 98",
      fullSolution: "9408 (First part: 94, Last part: 08)",
    },
  },
};
